import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl'
import { getCurrentIntl } from 'i18n/util/currentIntl'

type TourBrand = 'luxurygold' | 'contiki' | 'insightvacations' | 'trafalgar' | 'costsaver' | 'aatkings'

type FAQItem = App.Tours.TourV2OfferFrequentlyAskedQuestion
const FAQ_CONTENT_KEYS = ['operator', 'accommodation', 'experience', 'service', 'group'] as const
type FAQContentKey = typeof FAQ_CONTENT_KEYS[number]

const faqFallbackQuestionMessages = defineMessages({
  operator: {
    id: 'vKXTy0RRQ3qUw99Ui88C',
    defaultMessage: 'Who is {name}?',
    description: 'Fallback tour FAQ question asking who the tour operator is',
  },
  accommodation: {
    id: 'gjPG0nMqM5XsHt-cMGTh',
    defaultMessage: 'What accommodation should I expect?',
    description: 'Fallback tour FAQ question asking what accommodation is included',
  },
  experience: {
    id: 'DSWEshjEYwlH-89LNJI0',
    defaultMessage: 'What will I experience with {name}?',
    description: 'Fallback tour FAQ question asking what experiences are included with a tour operator',
  },
  service: {
    id: 'Hw9toHydJKyWcOpdJDrt',
    defaultMessage: 'What service can I expect when touring with {name}?',
    description: 'Fallback tour FAQ question asking what service is expected with a tour operator',
  },
  group: {
    id: 'GA3Ejyklh8H4vihKBBXc',
    defaultMessage: 'How many people will I travel with?',
    description: 'Fallback tour FAQ question asking about the group size',
  },
})

const BRAND_DISPLAY_NAME_MESSAGES: Record<TourBrand, MessageDescriptor> = defineMessages({
  trafalgar: {
    id: 'aMX2fhNGtLnUQ59IVX_i',
    defaultMessage: 'Trafalgar',
    description: 'Fallback tour FAQ display name for the Trafalgar tour operator brand',
  },
  aatkings: {
    id: 'XEKZWLvrGwpJwDOnEO0C',
    defaultMessage: 'AATKings',
    description: 'Fallback tour FAQ display name for the AATKings tour operator brand',
  },
  insightvacations: {
    id: 'VL9aTHyFJb_Obx4Xv23t',
    defaultMessage: 'Insight',
    description: 'Fallback tour FAQ display name for the Insight tour operator brand',
  },
  contiki: {
    id: 'yeqG6zD0nRfDHTWbZbdz',
    defaultMessage: 'Contiki',
    description: 'Fallback tour FAQ display name for the Contiki tour operator brand',
  },
  luxurygold: {
    id: 'ftnKa25m9w1Tv_0ECVOH',
    defaultMessage: 'Luxury Gold',
    description: 'Fallback tour FAQ display name for the Luxury Gold tour operator brand',
  },
  costsaver: {
    id: '1JyrETI2iVKfbuLWQIc9',
    defaultMessage: 'Cost Saver',
    description: 'Fallback tour FAQ display name for the Cost Saver tour operator brand',
  },
})

const faqFallbackContentMessages = defineMessages({
  luxurygoldOperator1: {
    id: 'QwccQJxk-ja51fyYkPnH',
    defaultMessage: "Welcome to the New Golden Age Of Travel with Luxury Gold. Enjoy the finest end to end experience on the world's leading limited edition small group tours. With an average of 20 guests, every journey is filled with unrivalled Passport Moments, the golden threads that create your extraordinary vacation. The sounds of Mozart soaring after hours under frescoed palace ceilings. Flavors of spice from Delhi's street vendors. Sustainable ways to live, cook and savor at a pioneering Michelin starred restaurant. If passports were stamped with experiences instead of place, they would be imprinted with these exceptional moments. Curated exclusively for you.",
    description: 'Fallback tour FAQ answer paragraph for Luxury Gold operator information',
  },
  luxurygoldAccommodation1: {
    id: 'jGQyUwesEn5krruumxzf',
    defaultMessage: "Your every need will be attended to with our carefully curated selection of places to stay. Enjoy magnificent locations, glamorous properties, and superior service. Belmond, JW Marriott, Intercontinental and Red Carnation Luxury Hotels and are just a few of our trusted partners, along with unique boutique accommodations. Whether you want to put your feet up or let your hair down, your encounter with excellence begins the moment you wake up and continues through each decadent night's rest.",
    description: 'Fallback tour FAQ answer paragraph for Luxury Gold accommodation information',
  },
  luxurygoldExperience1: {
    id: '36-RX0bQ121sHIP-oOTr',
    defaultMessage: 'Curated travel with VIP access. Unique luxury experiences at your fingertips. This is VIP vacations like no other.',
    description: 'Fallback tour FAQ answer paragraph for Luxury Gold experience information',
  },
  luxurygoldExperience2: {
    id: 'CxaOfPVyOotB1F3DczuY',
    defaultMessage: "Enjoy rare insights into the world's most majestic landmarks, with personal anecdotes from those in the know. Go beyond the velvet rope on experiences including an exclusive after hours visit to the Tower of London for the Ceremony of the Keys, joining an Egyptologist deep inside one of the Pyramids to explore hieroglyphs etched on the walls in covert chambers, and dining with na All Blacks Rugby player in Auckland, New Zealand.",
    description: 'Fallback tour FAQ answer paragraph for Luxury Gold experience information',
  },
  luxurygoldExperience3: {
    id: 'fFrUMUyA5qaWKH3ls0Ae',
    defaultMessage: 'Go beyond your expectations with the Founders Collection. Curated by our founder, the late Stanley Tollman, these are his friendships from across the globe brought to you through exclusive meet and greets.',
    description: 'Fallback tour FAQ answer paragraph for Luxury Gold experience information',
  },
  luxurygoldService1: {
    id: '8RFMh0055_hbgFiqlw7Y',
    defaultMessage: "Your Travel Concierge is an expert on your destination. And your tastes. At your service, 24/7, with you every step of the way. They will lead you through under-the-radar history, introduce you to undiscovered artisans, and connect you to the humanity and culture of your destination. They know what you need before you need it. Every detail or desire is tailored for you. They are the embodiment of 'driven by service' – the founding philosophy on which our company is built.",
    description: 'Fallback tour FAQ answer paragraph for Luxury Gold service information',
  },
  luxurygoldGroup1: {
    id: 'ur2lVMiCVqVSbFEdbf60',
    defaultMessage: 'Small Group Journeys - Enjoy a bespoke experience within an intimate setting of an average of 20 guests, meaning a personal one-to-one experience. Exclusively for you.',
    description: 'Fallback tour FAQ answer paragraph for Luxury Gold group information',
  },
  insightvacationsOperator1: {
    id: 'JuNVghG3QJkpF8ZP47sG',
    defaultMessage: 'For more than 40 years, we have delighted our loyal travellers with all the sights and the insights, up close and personal, in comfort and in style. We are renowned for our smaller groups, our specially designed, customised luxury coaches with twice the legroom and twice the view, and our brilliant collection of more than 70 authentic and unique Insight Experiences, our deliciously authentic dining options, and the way we stay in style. We are committed to supporting initiatives which protect the people, planet, and wildlife so they remain vibrant for generations to come.',
    description: 'Fallback tour FAQ answer paragraph for Insight operator information',
  },
  insightvacationsAccommodation1: {
    id: 'vqW-gyuoeLKON9HS0Q-U',
    defaultMessage: 'Stay in style - We take the greatest possible care in choosing highly rated 4.5+ star hotels with excellent style, location, and service. We want you to love your home away from home.',
    description: 'Fallback tour FAQ answer paragraph for Insight accommodation information',
  },
  insightvacationsExperience1: {
    id: 'GddvcwEJ9RL5h9EeOdhU',
    defaultMessage: 'With more 2- and 3-night stays than any other operator, Insight gives you the time to spread out, relax and not have to pack and unpack each day. You have time to enjoy your fine hotel and all the exciting shopping, dining, entertainment, and cultural options that await just outside its doors. Great food is at the heart of every journey. From thoughtful vegetarian options to seasonal specialties in amazing locations, you will sip, savour, and sense the local flair at every meal. Meet fascinating people and step into their homes and their lives. Learn about history and pioneering projects, take a cooking class or learn to dance with over 100 included Insight Experiences. Travel in style with more personal space - Your Insight coach is uniquely customised for extra space. Sit high above the traffic with unobstructed views and enjoy twice the legroom and personal space of a regular coach.',
    description: 'Fallback tour FAQ answer paragraph for Insight experience information',
  },
  insightvacationsService1: {
    id: 'c_fKxJ4m_I6OYv7heRoT',
    defaultMessage: "From the moment you arrive, our talented people are there to welcome you and ensure that you enjoy your guided holiday to the fullest. Your 'travelling concierge' lives in the destination and is with you all the way, a friendly face in a foreign land and a great travelling companion. They're exceptionally experienced, well-organised, knowledgeable and at the pinnacle of their careers. They make everything run smoothly and take time out to help you with your specific priorities and interests.",
    description: 'Fallback tour FAQ answer paragraph for Insight service information',
  },
  insightvacationsGroup1: {
    id: 'sCBXoMp8qsTEup-XAR73',
    defaultMessage: 'We are renowned for our smaller groups, and our specially designed, customised luxury coaches with twice the legroom and twice the view. We now give you the option to travel in your preferred group size. Classic group tours feature a maximum of 40 guests, small group tours have no more than 24 guests, while private groups have a cap of just 12 guests. Please contact us to enquire about private tours.',
    description: 'Fallback tour FAQ answer paragraph for Insight group information',
  },
  trafalgarOperator1: {
    id: 'QkG-M23n9fu_sWpfJDaJ',
    defaultMessage: "From dreaming, to planning, to booking, we're with you every step of the way with our 24/7 travel expertise and dedicated service. We have the experience and know how to adapt to change while ensuring our guests holiday is always stress free, so they can focus on what matters – having a great time and creating memories that will last a lifetime. We know that our guests don't just want to see places. They want to be a part of them, to savour not just sights and sounds but experiences, to get insider looks at cultures, customs, and wonders. This is why we do not just take our guests to see places...instead we have locals open their doors to welcome them in like friends. From small, family-run hotels to passionate keepers of cultures and traditions, we craft memorable encounters and connections that our guests will cherish for years to come.",
    description: 'Fallback tour FAQ answer paragraph for Trafalgar operator information',
  },
  trafalgarAccommodation1: {
    id: 'zzqpmEyXOBxf_GhDjOid',
    defaultMessage: 'Stay in accommodation that is more than just a bed for the night. We select our hotels not only for their quality, impeccable service and comfort but they are also an important piece of the local heritage. From centuries-old castles to historical homesteads, you will be immersed in the stories behind each property, giving you the chance to live the legacy yourself.',
    description: 'Fallback tour FAQ answer paragraph for Trafalgar accommodation information',
  },
  trafalgarExperience1: {
    id: 'BuLddqd-rXhc9A5n1Yii',
    defaultMessage: "Each trip is carefully crafted to leave you free to live in the moment and enjoy your holiday. Dive into the local cultures that make each destination so special. Enjoy our Trafalgar-exclusive experiences for a deeper understanding of the places we go, while allowing you to see the world through a different lens. We connect you with locals who are proud to welcome you into their community and share their stories through food – not only to experience the regions' delicacies, but also the way of life. See the iconic, bucket list sights with a local specialist. Someone who won't just give you the kind of information you might glean from guidebooks, but show you how it's done, or supply you with fresh insights and anecdotes to make your guided holiday memorable. Food tastes so much better when it's shared with local people who have a story to tell. Perhaps that's why Trafalgar's \"Be My Guest\" experiences are so popular. You'll dine with local families in their homes, learn how to cook regional dishes, and share a glass or two with winemakers at their vineyards; giving you a deeper understanding of their lives and history.",
    description: 'Fallback tour FAQ answer paragraph for Trafalgar experience information',
  },
  trafalgarService1: {
    id: 'aJhzGEsiTJC23FInIl_j',
    defaultMessage: "You will gain access to travel expertise and dedicated service to support you at every step, making travel in these recent times worry-free. The Trafalgar difference is not just what we promise, it's what we deliver. The best way to unlock rich travel experiences around the world starts here.",
    description: 'Fallback tour FAQ answer paragraph for Trafalgar service information',
  },
  trafalgarService2: {
    id: 'RbkQR5Xq9RpFukzbs40C',
    defaultMessage: "Our Travel Directors are carefully selected for their knowledge and enthusiasm. Whether they're introducing you to a local artisan, revealing a hidden treasure or simply helping you to plan your day, they are your ideal friend and companion, wherever you are on your travels. From checking you into your hotel room to recommending a local eatery, your Travel Director's aim is to make travelling easy.",
    description: 'Fallback tour FAQ answer paragraph for Trafalgar service information',
  },
  trafalgarGroup1: {
    id: 'lLM4niN2f5vxM7lnNO3K',
    defaultMessage: 'Depending on the tour you choose and the time of year you are travelling our group sizes can vary from 20 people up to 52 guests, so there is a great opportunity to mingle and make new friends from all over the world. With such a diverse range of destinations and touring styles there is no set age or type who travel with Trafalgar, you can expect a wide-range of like-minded travellers. Families with children, young couples, baby boomers, solo travellers, small groups and more can be found on our guided tours. There is certainly no age limit for touring, with children as young as 6 through to seniors travelling, however, the general demographic tends to be over 40.',
    description: 'Fallback tour FAQ answer paragraph for Trafalgar group information',
  },
  costsaverOperator1: {
    id: 'vD_Dv0ronPAB4pw4fwwg',
    defaultMessage: "Costsaver gives you the power to choose how you travel. We think amazing travel memories shouldn't cost the earth, so our budget trips cover all the essentials while providing you the building blocks to tailor your dream trip. It's the ultimate unpackaged tour - an experience that you can customise and make your own.",
    description: 'Fallback tour FAQ answer paragraph for Cost Saver operator information',
  },
  costsaverAccommodation1: {
    id: '35MShgQgzmD8yShvukXj',
    defaultMessage: "With over 100 years of experience and local knowledge on tap, you can count on our team to deliver solid itineraries that tick the boxes. We select our hotels for their quality, amazing service and comfort, and the majority of the hotels have air-conditioning. Plus, we're always keeping an eye out for ways to make them even better. So give Google a break and leave the research and planning to us. It's kind of our thing, after all.",
    description: 'Fallback tour FAQ answer paragraph for Cost Saver accommodation information',
  },
  costsaverExperience1: {
    id: 'dFhzso5NXMhy2tQ96PrU',
    defaultMessage: "We know that when it comes to travel, different people enjoy different things. That's why we take care of all your travel essentials, while you tailor the rest. A little bit of this, a little bit of that. Yep, we think you should experience the world, your way. You simply choose the experiences you want from a curated range of over 500 Choice Optionals, and we'll make it happen. Enjoy perfect itineraries that have been carefully created so that you get the best out of the places you choose to go.",
    description: 'Fallback tour FAQ answer paragraph for Cost Saver experience information',
  },
  costsaverService1: {
    id: 'bN-tugt6ArFQngUnvugm',
    defaultMessage: "You will gain access to travel expertise and dedicated service to support you at every step, making travel in these recent times worry-free. Our amazing Travel Directors come from a range of backgrounds but they all share a deep passion for travel and their home countries. These experienced travel professionals offer you what the guidebooks can't – a true insider's perspective of each place you visit. Each Travel Director has undertaken a bespoke training program and most have at least five years' experience in guided travel.",
    description: 'Fallback tour FAQ answer paragraph for Cost Saver service information',
  },
  costsaverGroup1: {
    id: 'OWorcyN_VMQmVOtWQt04',
    defaultMessage: "The beauty of taking a guided holiday with us is that you'll have the opportunity to meet and travel with people from all over the globe. Our tours run with an average of 40-45 guests depending on the destination.",
    description: 'Fallback tour FAQ answer paragraph for Cost Saver group information',
  },
  aatkingsOperator1: {
    id: 'L46a3F6WE1JhMsa-4j6F',
    defaultMessage: 'At AAT Kings we strive to represent the very best that Australia and New Zealand have to offer. Our people are warm, friendly, engaged and enthusiastic. We love a laugh and a good story. We celebrate our great characters and our authentic personalities. We are always respectful, we deliver brilliant service and we are proud of our heritage and our culture.',
    description: 'Fallback tour FAQ answer paragraph for AATKings operator information',
  },
  aatkingsOperator2: {
    id: 'RR2t7aLilFaEfJP8pwt_',
    defaultMessage: "Our goal every day is to share Australia and New Zealand with our guests and to bring these two amazing countries to life. You can relax in the comfort of knowing you're in the best of hands. We've got over 100 years of experience in taking care of absolutely everything so that you can get on with creating great new memories.",
    description: 'Fallback tour FAQ answer paragraph for AATKings operator information',
  },
  aatkingsAccommodation1: {
    id: '86TdSmLrRB9jrDML9N36',
    defaultMessage: 'At AAT Kings we pride ourselves on our superior hotel and motel selection. Most of our hotels are centrally located and close to major attractions. As we travel from major cities to remote regions, the standards can vary, however you can rest assured that the hotels we have selected are the best available. All hotel and motel rooms are clean and well serviced. Each room contains a private bathroom with toilet, shower and/or bath and vanity. Rooms also often contain tea and coffee making facilities, radio, mini bar and room service. Wherever possible we select establishments which offer additional facilities too, such as swimming pools, sauna, restaurants, and bars.',
    description: 'Fallback tour FAQ answer paragraph for AATKings accommodation information',
  },
  aatkingsExperience1: {
    id: 'WIzrKMW-5zvVgHK-PTXh',
    defaultMessage: 'AAT Kings fleet of luxury coaches include some of the newest on the road. Our vehicles are equipped with modern features such as comfortable reclining seats with seat belts and footrests, panoramic windows, an on-board restroom, two-door access and state-of-the-art technology including DVD players, GPS and PA systems.',
    description: 'Fallback tour FAQ answer paragraph for AATKings experience information',
  },
  aatkingsExperience2: {
    id: 'gDRD0UgbJRvQYBTvnvV6',
    defaultMessage: "Our coaches also have cold water available to re-fill your water bottle or cup. We regularly upgrade our vehicles to ensure they meet the highest safety standards. Most recently, we've invested $8.5million on acquiring new, top of the range coaches for our fleet.",
    description: 'Fallback tour FAQ answer paragraph for AATKings experience information',
  },
  aatkingsExperience3: {
    id: 'pvvRlMXTWfQucw2v-kFO',
    defaultMessage: 'AAT Kings pride ourselves on offering the perfect mix of sightseeing and free time. While many amazing must-see experiences are included, there is also time to shop, explore, relax in a local café or take a walk.',
    description: 'Fallback tour FAQ answer paragraph for AATKings experience information',
  },
  aatkingsService1: {
    id: 'PuKyx_S_v5NR9VqHl9EY',
    defaultMessage: "At AAT Kings we've assembled a range of fun, passionate, quirky, knowledgeable and talented people to help make your holiday great and one you'll remember forever. Our team of Driver Guides and Local Guides will bring your holiday to life and they'll share their passion for Australia and New Zealand in ways beyond the expected.",
    description: 'Fallback tour FAQ answer paragraph for AATKings service information',
  },
  aatkingsService2: {
    id: 'a_EPEVoCmY5puFzYpifl',
    defaultMessage: "We pride ourselves on having some of the most experienced Driver Guides in the world. They'll provide the commentary, stories and insights behind each destination to allow you to truly get a feel for the places you visit, while also being an invaluable resource of knowledge and information throughout your holiday.",
    description: 'Fallback tour FAQ answer paragraph for AATKings service information',
  },
  aatkingsGroup1: {
    id: 'yEBTTXSHnP0dpaaM1mic',
    defaultMessage: 'Depending on the tour you choose and the time of year you are travelling our group sizes can vary from 20 people up to 49 people, so there is a great opportunity to mingle and make new friends.',
    description: 'Fallback tour FAQ answer paragraph for AATKings group information',
  },
  aatkingsGroup2: {
    id: 'JcIedUz12hCpshen96A4',
    defaultMessage: 'With such a diverse range of destinations and touring styles there is no set age or type who travel with AAT, you can expect a wide-range of like-minded travellers. Families with children, young couples, baby boomers, solo travellers, small groups and more can be found on our guided tours. There is certainly no age limit for touring, with children as young as 6 through to seniors travelling, however, the general demographic tends to be over 40. As AAT Kings is sold internationally you will also find that there are people from all over the world on their tours, particularly North Americans, British, Europeans and many Asian nations.',
    description: 'Fallback tour FAQ answer paragraph for AATKings group information',
  },
})

const FAQ_CONTENT: Record<TourBrand, Partial<Record<FAQContentKey, { content: Array<MessageDescriptor>, key: FAQContentKey }>>> = {
  luxurygold: {
    operator: {
      content: [faqFallbackContentMessages.luxurygoldOperator1],
      key: 'operator',
    },
    accommodation: {
      content: [faqFallbackContentMessages.luxurygoldAccommodation1],
      key: 'accommodation',
    },
    experience: {
      content: [faqFallbackContentMessages.luxurygoldExperience1, faqFallbackContentMessages.luxurygoldExperience2, faqFallbackContentMessages.luxurygoldExperience3],
      key: 'experience',
    },
    service: {
      content: [faqFallbackContentMessages.luxurygoldService1],
      key: 'service',
    },
    group: {
      content: [faqFallbackContentMessages.luxurygoldGroup1],
      key: 'group',
    },
  },
  contiki: {
  },
  insightvacations: {
    operator: {
      content: [faqFallbackContentMessages.insightvacationsOperator1],
      key: 'operator',
    },
    accommodation: {
      content: [faqFallbackContentMessages.insightvacationsAccommodation1],
      key: 'accommodation',
    },
    experience: {
      content: [faqFallbackContentMessages.insightvacationsExperience1],
      key: 'experience',
    },
    service: {
      content: [faqFallbackContentMessages.insightvacationsService1],
      key: 'service',
    },
    group: {
      content: [faqFallbackContentMessages.insightvacationsGroup1],
      key: 'group',
    },
  },
  trafalgar: {
    operator: {
      content: [faqFallbackContentMessages.trafalgarOperator1],
      key: 'operator',
    },
    accommodation: {
      content: [faqFallbackContentMessages.trafalgarAccommodation1],
      key: 'accommodation',
    },
    experience: {
      content: [faqFallbackContentMessages.trafalgarExperience1],
      key: 'experience',
    },
    service: {
      content: [faqFallbackContentMessages.trafalgarService1, faqFallbackContentMessages.trafalgarService2],
      key: 'service',
    },
    group: {
      content: [faqFallbackContentMessages.trafalgarGroup1],
      key: 'group',
    },
  },
  costsaver: {
    operator: {
      content: [faqFallbackContentMessages.costsaverOperator1],
      key: 'operator',
    },
    accommodation: {
      content: [faqFallbackContentMessages.costsaverAccommodation1],
      key: 'accommodation',
    },
    experience: {
      content: [faqFallbackContentMessages.costsaverExperience1],
      key: 'experience',
    },
    service: {
      content: [faqFallbackContentMessages.costsaverService1],
      key: 'service',
    },
    group: {
      content: [faqFallbackContentMessages.costsaverGroup1],
      key: 'group',
    },
  },
  aatkings: {
    operator: {
      content: [faqFallbackContentMessages.aatkingsOperator1, faqFallbackContentMessages.aatkingsOperator2],
      key: 'operator',
    },
    accommodation: {
      content: [faqFallbackContentMessages.aatkingsAccommodation1],
      key: 'accommodation',
    },
    experience: {
      content: [faqFallbackContentMessages.aatkingsExperience1, faqFallbackContentMessages.aatkingsExperience2, faqFallbackContentMessages.aatkingsExperience3],
      key: 'experience',
    },
    service: {
      content: [faqFallbackContentMessages.aatkingsService1, faqFallbackContentMessages.aatkingsService2],
      key: 'service',
    },
    group: {
      content: [faqFallbackContentMessages.aatkingsGroup1, faqFallbackContentMessages.aatkingsGroup2],
      key: 'group',
    },
  },
}

function buildFaqForBrand(brand: TourBrand, intl: IntlShape = getCurrentIntl()): Array<FAQItem> {
  const displayName = intl.formatMessage(BRAND_DISPLAY_NAME_MESSAGES[brand])
  const brandContent = FAQ_CONTENT[brand]

  return FAQ_CONTENT_KEYS
    .filter(key => brandContent[key] != null)
    .map(key => ({
      title: intl.formatMessage(faqFallbackQuestionMessages[key], { name: displayName }),
      content: brandContent[key]!.content.map(message => intl.formatMessage(message)),
      key: brandContent[key]!.key,
    }))
}

const FAQ_FALLBACK_BRANDS = new Set<TourBrand>([
  'luxurygold',
  'insightvacations',
  'trafalgar',
  'costsaver',
  'aatkings',
])

export function getTourFaqFallback(brandCode: string): Array<FAQItem> | undefined {
  const brand = brandCode as TourBrand
  if (!FAQ_FALLBACK_BRANDS.has(brand)) return undefined
  return buildFaqForBrand(brand)
}
